import { Dayjs } from "dayjs";
import { getDataFromLocalDb } from "../../../services/dbService";

export const calculateBatchTotals = (batch: any) => {
  if (!batch || !Array.isArray(batch.reasonDetails)) {
    return { totalCaseQty: 0, totalPieceQty: 0, totalOtherQty: 0 };
  }
  return batch.reasonDetails.reduce(
    (totals: any, reason: any) => {
      totals.totalCaseQty += reason.caseQty || 0;
      totals.totalPieceQty += reason.pieceQty || 0;
      totals.totalOtherQty += reason.otherQty || 0;
      return totals;
    },
    { totalCaseQty: 0, totalPieceQty: 0, totalOtherQty: 0 }
  );
};

export const calculateTotals = (batches: any[]) => {
  return batches.reduce(
    (totals, batch) => {
      batch.reasonDetails.forEach((reason: any) => {
        totals.totalCaseQty += reason.caseQty || 0;
        totals.totalPieceQty += reason.pieceQty || 0;
        totals.totalOtherQty += reason.otherQty || 0;
      });
      return totals;
    },
    { totalCaseQty: 0, totalPieceQty: 0, totalOtherQty: 0 }
  );
};

export const calculateTotalCsAndPcAndOther = (batchIds: any) => {
  let totalCs = 0;
  let totalPc = 0;
  let totalOther = 0;

  batchIds.forEach((batch: any) => {
    totalCs += batch.caseQty || 0; 
    totalPc += batch.pieceQty || 0;
    totalOther += batch.otherQty || 0;
  });

  return { totalCs, totalPc, totalOther };
};

export const getTotalAmount = (rows: any[], key: string): number => {
  return parseFloat(
    rows.reduce((acc: number, item: any) => {
      const amount = item?.[key] || 0;
      return acc + amount;
    }, 0)
  );
};

export const modifyReturnProductsArray = (
  inputArray: any[],
  editedBatches: any[]
) => {
  return inputArray.map((item: any) => {
    const editedBatchEntry = editedBatches.find(
      (edited) => edited.skuCode === item.skuCode
    );
    const editedBatchIds = editedBatchEntry?.batchIds || [];

    let totalCs = 0;
    let totalPc = 0;
    let totalOther = 0;
    const filteredBatchIds: any[] = [];

    item.batchIds.forEach((batch: any) => {
      if (editedBatchIds.includes(batch.id)) {
        filteredBatchIds.push(batch);
        batch.reasonDetails.forEach((reason: any) => {
          totalCs += parseInt(reason.caseQty || 0, 10);
          totalPc += parseInt(reason.pieceQty || 0, 10);
          totalOther += parseInt(reason.otherQty || 0, 10);
        });
      }
    });

    return {
      ...item,
      cases: totalCs,
      pieces: totalPc,
      others: totalOther,
      batchIds: filteredBatchIds, 
      totalClaimAmount: item.stockType === "foc" ? 0 : item.totalClaimAmount
    };
  });
};

export const downloadFile = async (fileUri: string, fileName: string) => {
  try {
    const response = await fetch(fileUri);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading file:', error);
  }
};

export const getBlobUrlForSku = async (skuCode: string) => {
  try {
    const productData = await getDataFromLocalDb("Product");
    const allProducts = productData?.data?.[0]?.data;
    for (const key in allProducts) {
      const product = allProducts[key];
      if (product?.skuCode === skuCode || product?.batchCode === skuCode) {
        return product.blobKey;
      }
    }
  } catch (error) {
    console.error("Failed to fetch blobKey:", error);
  }
};  

export const transformApprovalData = (newApprovalList: any[]) => {
  const approvalList: any[] = [];

  newApprovalList.forEach(approvalEntry => {
    approvalEntry.approvalDetails.forEach((detail: any) => {
      const approval = {
        id: detail.id,
        userId: approvalEntry.currentApproverId,
        designation: approvalEntry.designation,
        approvedQuantity: detail.approvedValue,
        approvedAmount: detail.metadata?.approvedAmount || null,
        claimDetailsId: detail.referenceId,
        level: approvalEntry.currentLevel,
        requestId: approvalEntry.requestId,
        reason: detail.comments,
        status: detail.status.toUpperCase()
      };
      approvalList.push(approval);
    });
  });
  return approvalList;
};  

export const returnProductObj = {
  skuCode: "",
  skuName: "",
  batchIds: [],
};

export const claimSummarySkeleton = {
  "SKU Name": "Charged by Thumbs Up Pet 250 ml",
  "Claim Type": "Discounts",
  "Selected Month": "Aug - 2024",
  Invoices: "",
  "Total Qty": "45 Cs, 26 Pc",
  "Total Amount": "₹50,340.09",
  "Total Order Discount": "₹6,640.76",
};

export const getLastNPathSegments = (url: string, n: number): string[] => {
  const parsedUrl = new URL(url);
  const pathSegments = parsedUrl.pathname
    .split("/")
    .filter((segment) => segment);
  const lastNPathSegments = pathSegments.slice(-n).reverse();
  return lastNPathSegments;
};

export const determineClaimTypes = (path: string, claimTypes: any, claimSubTypes: any) => {
  switch (path) {
    case "saleReturns":
      return { claimType: claimTypes[0], subClaimType: claimSubTypes[0] };
    case "purchaseReturns":
      return { claimType: claimTypes[0], subClaimType: claimSubTypes[1] };
    case "secondarySchemes":
      return { claimType: claimTypes[1], subClaimType: claimSubTypes[2] };
    case "totalOrderValue":
      return { claimType:claimTypes[1], subClaimType: claimSubTypes[3] };
    case "displayVisibility":
      return { claimType: claimTypes[2], subClaimType: claimSubTypes[4] };
    case "assetInstallation":
      return { claimType: claimTypes[2], subClaimType: claimSubTypes[5] };
    default:
      return { claimType: "", subClaimType: "" };
  }
};

export interface SKU {
  skuCode: string;
  skuName: string;
}

export type ApprovalConfig = {
  flow: string[][];
  currentLevel?: number;
  lowestLevel?: number;
};

export interface DisplayVisibilityFormData {
  id: string;
  claimDate: Dayjs | null;
  outletCode: string;
  outletName: string;
  skus: SKU[]; 
  displayStartDate: Dayjs | null;
  displayEndDate: Dayjs | null;
  installationCost: number;
  displayCost: number;
  remarks: string;
  blobUrls: string[];
  totalClaimAmount: number;
  bannerName: string;
  [key: string]: any;
}

export interface AssetInstallationFormData {
  id: string;
  claimDate: Dayjs | null;
  outletCode: string;
  outletName: string;
  skuCode: string;
  skuName: string;
  quantity: number;
  installationDate: Dayjs | null;
  installationCost: number;
  displayCost: number;
  remarks: string;
  blobUrls: string[];
  totalClaimAmount: number;
  [key: string]: any | null;
}

export interface ClaimDetail {
  referenceNumber?: string;
  batchCode?: string;
  skuCode?: string;
  skuName?: string;
  promotionId?: string;
  claimedQty?: string;
  approvedQty?: number;
  claimedAmount: number;
  approvedAmount: number;
  remark: string;
  assetId?: string; 
  assetName?: string; 
  assetType?: string; 
  assetQty?: number; 
  outletCode?: string; 
  outletName?: string; 
  installationCost?: number; 
  displayCost?: number; 
  displayStartDate?: string; 
  displayEndDate?: string;
  blobUrl?: string[];
  extendedAttributes?: {}; 
}

export const getSecondarySchemesColumns = (isNonEligible: boolean) => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {secondarySchemesUIConfig} = ConfigDictionary;
  const columns = [
    {
      headerName: "SKU Code",
      mappedValue: "skuCode",
      width: 190,
      headerAlign: "left",
      align: "left",
      hasInfoTooltip: isNonEligible,
      infoTooltipMessage: "SKUs marked with red warning icon are non-eligible.",
    },
    {
      headerName: "SKU Name",
      mappedValue: "skuName",
      width: 350,
      headerAlign: "left",
      align: "left",
    },
    {
      headerName: "Invoices",
      mappedValue: "invoiceNo",
      width: 120,
      headerAlign: "left",
      align: "left",
    },
    ...(secondarySchemesUIConfig.showTotalQuantity
      ? [
          {
            headerName: "Total Qty",
            mappedValue: "cases,pieces,others",
            width: 215,
            headerAlign: "left",
            align: "left",
            type: "order",
          },
        ]
      : []),
    {
      headerName: "Scheme Type",
      mappedValue: "schemeType",
      width: 232,
      headerAlign: "left",
      align: "left",
    },
    {
      headerName: "Discount",
      mappedValue: "finalAmount",
      width: 200,
      headerAlign: "left",
      align: "left",
      includeCurrenySymbol: true,
    },
    {
      headerName: "",
      mappedValue: "editColumn",
      width: 94,
      headerAlign: "left",
      align: "center",
      type: secondarySchemesUIConfig.isEditable ? "edit" : "view",
    },
  ];

  return {
    columns,
    Actions: ["select"],
  };
};

export const getTotalOrderValueColumns = () => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {secondarySchemesUIConfig} = ConfigDictionary;
  const columns = [
    {
      headerName: "Invoice No.",
      mappedValue: "invoiceNo",
      width: 200,
      headerAlign: "left",
      align: "left",
    },
    {
      headerName: "Invoice Date & Time",
      mappedValue: "invoiceDateTime",
      width: 200,
      headerAlign: "left",
      align: "left",
    },
    {
      headerName: "Outlet Name & Code",
      mappedValue: "outletNameCode",
      width: 250,
      headerAlign: "left",
      align: "left",
    },
    ...(secondarySchemesUIConfig.showTotalQuantity
      ? [
        {
          headerName: "Total Qty",
          mappedValue: "cases,pieces,others",
          width: 180,
          headerAlign: "left",
          align: "left",
          type: "order",
        }
        ]
      : []),
    {
      headerName: "Total Amount",
      mappedValue: "totalAmount",
      width: 200,
      headerAlign: "left",
      align: "left",
      includeCurrenySymbol:true
    },
    {
      headerName: "Discount",
      mappedValue: "discount",
      width: 200,
      headerAlign: "left",
      align: "left",
      includeCurrenySymbol:true
    },
  ];

  return {
    columns,
    Actions: ["view"],
  };
};

export const getInvoicesColumns = (batchIdConfiguration:boolean = false) => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const {secondarySchemesUIConfig} = ConfigDictionary;
  const columns = [
    {
      headerName: "Invoice No. & Date",
      headerParts: { top: "Invoice No.", bottom: "& Date" },
      mappedValue: "invoiceNumber,invoiceDate",
      width: 198,
      headerAlign: "left",
      align: "left",
      invoiceNumStyling: true,
    },
    {
      headerName: "Outlet Name",
      mappedValue: "outletName",
      width: 180,
      headerAlign: "left",
      align: "left",
      wrap: "true",
    },
    {
      headerName: "Discounts & Promotions",
      headerParts: { top: "Discounts &", bottom: "Promotions" },
      mappedValue: "discountsAndPromotions",
      width: 200,
      headerAlign: "left",
      align: "left",
    },
    ...(batchIdConfiguration
      ? [
          {
            headerName: "Batch ID",
            mappedValue: "batchIds",
            width: 160,
            headerAlign: "left",
            align: "left",
          },
        ]
      : []),
    {
      headerName: "Total Qty",
      mappedValue: "cases,pieces,others",
      width: 153,
      headerAlign: "left",
      align: "left",
      type: "order",
    },
    {
      headerName: "Price (Each Cs)",
      headerParts: { top: "Price ", bottom: "(Each Cs)" },
      mappedValue: "pricePerCase",
      width: 141,
      headerAlign: "left",
      align: "left",
      includeCurrenySymbol: true,
    },
    {
      headerName: "Price (Each Pc)",
      headerParts: { top: "Price ", bottom: "(Each Pc)" },
      mappedValue: "pricePerPiece",
      width: 143,
      headerAlign: "left",
      align: "left",
      includeCurrenySymbol: true,
    },
    {
      headerName: "Amount",
      mappedValue: "amount",
      width: 156,
      headerAlign: "left",
      align: "left",
      includeCurrenySymbol: true,
    },

    {
      headerName: "Discount",
      mappedValue: "discount",
      width: 142,
      headerAlign: "left",
      align: "left",
      includeCurrenySymbol: true,
    },
    ...(secondarySchemesUIConfig.showTotalQuantity
      ? [
          {
            headerName: "Final Amount",
            headerParts: { top: "Final ", bottom: "Amount" },
            mappedValue: "finalAmount",
            width: 148,
            headerAlign: "left",
            align: "left",
            includeCurrenySymbol: true,
            isEditable: true,
            hasEditPenSvg: true,
          },
          {
            headerName: "",
            mappedValue: "removeInvoice",
            width: 30,
            headerAlign: "left",
            align: "center",
          },
        ]
      : []),
  ];

  return {
    columns,
    Actions: [],
  };
};
